<script>
	import { onMount, tick } from 'svelte';
	import L from 'leaflet';
	import CamCard from './CamCard.svelte';
	import Chip from './Chip.svelte';

	let venues = [];
	let venue = null;
	let venueSlug = '';
	let openCams = [];
	let loadError = '';
	let booting = true;
	let switching = false;
	let mapOpen = true;
	let listOpen = true;
	let cardW = 31; // % width per feed card
	let targetFilter = 'all';

	let mapEl;
	let map;
	let markersLayer = null;
	const markerByName = {};

	const TARGETS = ['parking', 'concourse', 'approach', 'skyline', 'field'];
	const FILTERS = ['all', ...TARGETS];
	const MAX_OPEN = 12;
	const DEFAULT_COLOR = '#28d7f2';
	const ACTIVE_COLOR = '#FDB515';
	const DIM_OPACITY = 0.12;

	function toggle(cam) {
		const i = openCams.findIndex(c => c.name === cam.name);
		if (i > -1) openCams.splice(i, 1);
		else {
			if (openCams.length >= MAX_OPEN) openCams.shift();
			openCams.push(cam);
		}
		openCams = openCams;
		syncMarkers();
		syncHash();
	}

	function closeCam(cam) {
		openCams = openCams.filter(c => c.name !== cam.name);
		syncMarkers();
		syncHash();
	}

	function clearAll() {
		openCams = [];
		syncMarkers();
		syncHash();
	}

	function isOpen(cam) {
		return openCams.some(c => c.name === cam.name);
	}

	function camsFor(target) {
		return venue ? venue.cameras.filter(c => (c.target || 'approach') === target) : [];
	}

	function setFilter(f) {
		targetFilter = f;
		syncMarkers();
	}

	function visibleCams() {
		return targetFilter === 'all'
			? (venue?.cameras || [])
			: (venue?.cameras || []).filter(c => (c.target || 'approach') === targetFilter);
	}

	function syncMarkers() {
		for (const [name, marker] of Object.entries(markerByName)) {
			const cam = venue.cameras.find(c => c.name === name);
			const inFilter = targetFilter === 'all' || (cam && (cam.target || 'approach') === targetFilter);
			const active = openCams.some(c => c.name === name);
			marker.setStyle({
				fillColor: active ? ACTIVE_COLOR : DEFAULT_COLOR,
				color: active ? '#ffffff' : '#eaeaea',
				fillOpacity: inFilter ? 1 : DIM_OPACITY,
				opacity: inFilter ? 1 : DIM_OPACITY,
				weight: active ? 2 : 1
			});
			marker.unbindTooltip();
			if (inFilter) marker.bindTooltip(cam.name, { direction: 'top', offset: [0, -6] });
		}
	}

	function syncHash() {
		const h = openCams.map(c => encodeURIComponent(c.name)).join(',');
		const p = new URLSearchParams();
		p.set('venue', venueSlug);
		if (!mapOpen) p.set('map', 'false');
		if (!listOpen) p.set('list', 'false');
		if (cardW !== 31) p.set('w', String(cardW));
		history.replaceState(null, '', `?${p.toString()}${h ? '#' + h : ''}`);
	}

	function typeBadge(t) {
		return t === 'hls' ? '▶' : t === 'iframe' ? '⧉' : '●';
	}

	function buildMap() {
		if (map || !mapEl) return;
		map = L.map(mapEl, {
			center: [47.5952, -122.3321], // sensible default; venue selectView overrides
			zoom: 13,
			zoomControl: true
		});
		L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}', {
			minZoom: 1,
			maxZoom: 19,
			className: 'map-tiles',
			attribution: 'Tiles &copy; Esri'
		}).addTo(map);
		markersLayer = L.layerGroup().addTo(map);
		setTimeout(() => map.invalidateSize(), 50);
	}

	function buildMarkers() {
		if (!map) return;
		if (markersLayer) markersLayer.clearLayers();
		for (const k of Object.keys(markerByName)) delete markerByName[k];
		for (const cam of venue.cameras || []) {
			if (typeof cam.lat !== 'number' || typeof cam.lon !== 'number') continue;
			const marker = L.circleMarker([cam.lat, cam.lon], {
				radius: 5,
				fillOpacity: 1,
				fillColor: DEFAULT_COLOR,
				color: '#eaeaea',
				weight: 1
			});
			marker.on('click', () => toggle(cam));
			markersLayer.addLayer(marker);
			markerByName[cam.name] = marker;
		}
		syncMarkers();
	}

	async function selectVenue(slug, restoreHash = false) {
		switching = true;
		loadError = '';
		try {
			const r = await fetch(`venues/${slug}.json`);
			if (!r.ok) throw new Error(`HTTP ${r.status}`);
			const text = await r.text();
			if (text.trim().startsWith('<')) throw new Error('got HTML — manifest missing from this deployment');
			venue = JSON.parse(text);
			venueSlug = slug;
			if (!restoreHash) openCams = [];
			if (map) {
				map.setView([venue.center.lat, venue.center.lon], venue.zoom || 14);
				buildMarkers();
			}
			if (!restoreHash) syncHash();
			else syncMarkers();
		} catch (e) {
			venue = null;
			loadError = `Venue manifest '${slug}' failed to load (${e.message}).`;
		} finally {
			switching = false;
			booting = false;
		}
	}

	function toggleMap() {
		mapOpen = !mapOpen;
		if (map) setTimeout(() => map.invalidateSize(), 60);
		syncHash();
	}

	function widthInput(e) {
		cardW = +e.target.value;
		syncHash();
	}

	async function onVenueChange(e) {
		await selectVenue(e.target.value);
		await tick();
		if (map) setTimeout(() => map.invalidateSize(), 60);
	}

	onMount(async () => {
		try {
			const idx = await fetch('venues/index.json');
			if (idx.ok) venues = await idx.json();
		} catch (e) {
			venues = [];
		}

		const params = new URLSearchParams(location.search);
		let slug = params.get('venue');
		if (slug) {
			const match = venues.find(v => v.id.toLowerCase() === slug.toLowerCase());
			slug = match ? match.id : slug;
		} else if (venues.length) {
			slug = venues[0].id;
		} else {
			slug = 'sea';
		}

		if (params.get('map') === 'false') mapOpen = false;
		if (params.get('list') === 'false') listOpen = false;
		const wParam = params.get('w');
		if (wParam) cardW = Math.max(14, Math.min(60, +wParam));

		await selectVenue(slug, true);

		if (venue) {
			const names = location.hash.replace('#', '').split(',').filter(Boolean).map(decodeURIComponent);
			openCams = venue.cameras.filter(c => names.includes(c.name)).slice(0, MAX_OPEN);
			openCams = openCams;
			syncMarkers();
		}

		// Create the map only now, after boot finished and the container is
		// visible + sized. Building it while hidden produced wrong tile geometry
		// (chopped quadrants / blacked-out corners). invalidateSize after reveal.
		await tick();
		buildMap();
		if (map && venue) {
			map.setView([venue.center.lat, venue.center.lon], venue.zoom || 14);
			buildMarkers();
			setTimeout(() => map.invalidateSize(), 60);
		}

		if (window.WeatherWidget && typeof window.WeatherWidget.init === 'function') {
			try { window.WeatherWidget.init({ selector: '#weather-slot', venue: venueSlug }); } catch (e) {}
		}
	});
</script>

<div class="shell">
	<header>
<div class="brand">
		<span class="brand-name">ABET <em>/ VENUE FEEDS</em></span>
	</div>

		<select class="venue-select" value={venueSlug} on:change={onVenueChange}>
			{#each venues as v (v.id)}
				<option value={v.id}>{v.name} · {v.sport}</option>
			{/each}
			{#if !venues.length}<option value={venueSlug}>{venueSlug || '…'}</option>{/if}
		</select>

		<span class="open-count">OPEN <b class:full={openCams.length >= MAX_OPEN}>{openCams.length}</b>/{MAX_OPEN}</span>

		<input class="width-slider" type="range" min="14" max="60" value={cardW} on:input={widthInput} title="Feed card width" />

		<button class="tool-btn" class:on={listOpen} title="Camera list" on:click={() => { listOpen = !listOpen; syncHash(); }}>LIST</button>
		<button class="tool-btn" class:on={mapOpen} title="Show/hide map" on:click={toggleMap}>MAP</button>
		<button class="tool-btn danger" title="Close all feeds" on:click={clearAll}>DESELECT ALL</button>

		<div class="weather-slot" id="weather-slot" data-venue={venueSlug} title="Weather ensemble panel slot — Workstream B2">
			<span class="ws-label">WX ENSEMBLE</span>
			<span class="ws-value">— SLOT —</span>
		</div>
	</header>

	{#if loadError}
		<div class="error-bar">{loadError}</div>
	{/if}

	<div class="panes">
		{#if booting || switching}
			<div class="pane-overlay">
				<Chip size={96} message={switching ? 'Loading venue…' : 'Booting feeds'} />
			</div>
		{/if}

		<section class="map-frame" class:hidden={!mapOpen} class:busy={booting || switching}>
			<div class="map-inner">
				<div id="map" class="map-canvas" bind:this={mapEl}></div>

				<div class="filter-chips">
					{#each FILTERS as f}
						<button disabled={!venue}
							class:faded={targetFilter !== 'all' && f !== 'all'}
							class:onf={targetFilter === f}
							on:click={() => setFilter(f)}>
							{f.toUpperCase()}
						</button>
					{/each}
				</div>

				{#if venue}
					<div class="map-hint">TAP DOTS · AMBER = LIVE · {visibleCams().length} CAMS</div>
				{/if}
			</div>
		</section>

		{#if venue}
			<section class="cam-panel">
				<button class="panel-head" on:click={() => { listOpen = !listOpen; syncHash(); }}>
					CAMERAS <span class="panel-count">{venue.cameras.length}</span>
					<span class="panel-caret">{listOpen ? 'HIDE' : 'SHOW'}</span>
				</button>
				{#if listOpen}
					<div class="panel-body">
						{#each TARGETS as t}
							{@const cams = camsFor(t)}
							{#if cams.length && (targetFilter === 'all' || targetFilter === t)}
								<h3>{t.toUpperCase()} <span>{cams.length}</span></h3>
								{#each cams as cam (cam.name)}
									<button class="row" class:active={isOpen(cam)} on:click={() => toggle(cam)}>
										<span class="badge {cam.type}">{typeBadge(cam.type)}</span>
										<span class="row-name">{cam.name}</span>
										<span class="row-src">{cam.attribution}</span>
									</button>
								{/each}
							{/if}
						{/each}
					</div>
				{/if}
			</section>
		{/if}

		<main class="feed-pane">
			{#if venue && !(booting || switching)}
				{#each openCams as cam (cam.name)}
					<div class="feed-slot" style="width:{cardW}%;">
						<CamCard {cam} onClose={closeCam} />
					</div>
				{:else}
					<div class="empty">
						<p>TAP MAP DOTS — OR PICK FROM THE CAMERA LIST ABOVE<br />UP TO {MAX_OPEN} SIMULTANEOUS FEEDS</p>
					</div>
				{/each}
			{/if}
		</main>
	</div>

	{#if venue}
		<footer class="attributions">
			{#each [...new Set(venue.cameras.map(c => c.attribution))] as src}
				<span>{src}</span>
			{/each}
			<span class="dim">live public feeds · all rights respective owners</span>
		</footer>
	{/if}
</div>

<style>
	:global(html), :global(body) { height: 100%; overflow: hidden; }
	.shell { height: 100vh; display: flex; flex-direction: column; }

	header {
		flex: none;
		display: flex; align-items: center; flex-wrap: wrap; row-gap: 6px;
		gap: 8px;
		padding: 6px 10px;
		background: #0d131c; border-bottom: 1px solid #243447;
		position: relative; z-index: 1200;
	}

	.brand { display: flex; align-items: center; white-space: nowrap; }
	.brand-name {
		font-family: 'Times New Roman', serif;
		color: #FDB515; font-weight: 700; letter-spacing: 0.06em; font-size: 15px;
	}
	.brand-name em {
		font-family: ui-monospace, Menlo, Consolas, monospace;
		color: #667b90; font-style: normal; font-weight: 400;
		font-size: 10px; letter-spacing: 0.16em; margin-left: 6px;
	}

	.venue-select {
		background: #070B0F; color: #FDB515;
		border: 1px solid #2a3947; border-radius: 2px;
		font-family: inherit; font-size: 11px; padding: 3px 8px; max-width: 240px;
	}

	.open-count { color: #667b90; font-size: 10px; letter-spacing: 0.14em; white-space: nowrap; }
	.open-count b { color: #22c55e; }
	.open-count b.full { color: #e63946; }

	.width-slider { width: 90px; accent-color: #FDB515; }

	.tool-btn {
		background: transparent; border: 1px solid #2a3947; color: #dbe4ee;
		font-family: inherit; font-size: 10px; letter-spacing: 0.14em;
		padding: 4px 9px; cursor: pointer; border-radius: 2px; white-space: nowrap;
	}
	.tool-btn.on { background: #FDB515; border-color: #FDB515; color: #070B0F; font-weight: 700; }
	.tool-btn.danger:hover { border-color: #e63946; color: #e63946; }

	.weather-slot {
		margin-left: auto; display: flex; align-items: center; gap: 6px;
		border: 1px dashed #2a3947; border-radius: 2px; padding: 3px 8px;
	}
	.ws-label { color: #667b90; font-size: 9px; letter-spacing: 0.14em; }
	.ws-value { color: #9a7815; font-size: 10px; }
	

	.error-bar { flex: none; padding: 6px 12px; background: #3a1214; color: #f1a9a9; font-size: 12px; border-bottom: 1px solid #5c1a1a; }

	.panes {
		position: relative; flex: 1; min-height: 0;
		display: flex; flex-direction: column;
	}

	.pane-overlay {
		position: absolute; inset: 0; z-index: 1500;
		display: flex; align-items: center; justify-content: center;
		background: #070B0F;
	}

	/* --- sunken, rounded, full-width map -------------------------------- */
	.map-frame {
		flex: none; margin: 10px 10px 6px; padding: 8px;
		background: #030303;
		border: 1px solid #16202e; border-radius: 16px;
		box-shadow:
			inset 0 14px 30px -16px rgba(0, 0, 0, 0.95),
			inset 0 -12px 26px -14px rgba(0, 0, 0, 0.9),
			inset 0 1px 0 rgba(255, 255, 255, 0.04);
	}
	.map-frame.hidden { display: none; }
	.map-frame.busy { visibility: hidden; }

	.map-inner { position: relative; border-radius: 10px; overflow: hidden; }

	:global(.map-canvas) {
		height: clamp(240px, 42vh, 520px);
		width: 100%;
		background: #0d131c; z-index: 500;
	}

	.filter-chips {
		position: absolute; top: 12px; left: 54px; z-index: 600;
		display: flex; gap: 4px; flex-wrap: wrap;
	}
	.filter-chips button {
		background: rgba(10,10,10,0.85); border: 1px solid #2a3947; color: #dbe4ee;
		font-family: inherit; font-size: 9px; letter-spacing: 0.16em;
		padding: 3px 9px; cursor: pointer; border-radius: 999px;
	}
	.filter-chips button.onf { background: #FDB515; border-color: #FDB515; color: #070B0F; font-weight: 700; }
	.filter-chips button.faded:not(.onf) { opacity: 0.45; }

	.map-hint {
		position: absolute; bottom: 14px; left: 50%; transform: translateX(-50%);
		z-index: 600; pointer-events: none; white-space: nowrap;
		color: #FDB515; background: rgba(5,5,5,0.75); border: 1px solid #243447;
		font-size: 9px; letter-spacing: 0.18em; padding: 3px 10px; border-radius: 999px;
	}

	/* --- camera list panel (full-width, under the map) ------------------- */
	.cam-panel {
		flex: none; margin: 0 10px;
		background: #070707;
		border: 1px solid #1c1c1c; border-radius: 12px;
		overflow: hidden;
	}
	.panel-head {
		display: flex; align-items: center; gap: 8px; width: 100%;
		padding: 7px 12px; cursor: pointer;
		background: transparent; border: none; text-align: left;
		color: #FDB515; font-family: inherit; font-size: 11px; letter-spacing: 0.18em;
	}
	.panel-head:hover { background: #101010; }
	.panel-count { color: #22c55e; }
	.panel-caret { margin-left: auto; color: #667b90; font-size: 9px; letter-spacing: 0.14em; }
	.panel-body { max-height: min(34vh, 340px); overflow-y: auto; padding: 2px 6px 10px; }
	.panel-body h3 {
		margin: 10px 4px 4px; color: #667b90; font-size: 9px; font-weight: 400;
		letter-spacing: 0.22em;
	}
	.panel-body h3 span { color: #FDB515; margin-left: 4px; }
	.row {
		display: flex; align-items: center; gap: 7px; width: 100%;
		background: transparent; border: none; border-left: 2px solid transparent;
		color: #dbe4ee; font-family: inherit; font-size: 11px;
		padding: 4px 6px; cursor: pointer; text-align: left;
	}
	.row:hover { background: #16202e; }
	.row.active { border-left-color: #FDB515; background: #161303; }
	.row.active .row-name { color: #FDB515; }
	.badge { font-size: 8px; flex: none; }
	.badge.hls { color: #22c55e; } .badge.image { color: #60a5fa; } .badge.iframe { color: #c084fc; }
	.row-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.row-src { flex: none; color: #4b5563; font-size: 9px; letter-spacing: 0.08em; }

	.feed-pane {
		flex: 1; min-height: 160px;
		overflow-y: auto;
		display: flex; flex-wrap: wrap; gap: 8px; padding: 10px;
		align-content: flex-start;
		background: #070B0F;
	}

	.feed-slot { min-width: 230px; transition: width 80ms linear; }

	.empty {
		flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
		color: #667b90; text-align: center; font-size: 11px; letter-spacing: 0.18em; line-height: 2;
	}
	.empty p { margin: 0; }

	.attributions {
		flex: none; display: flex; flex-wrap: wrap; gap: 12px;
		padding: 4px 12px 6px; border-top: 1px solid #101822;
		color: #9a7815; font-size: 9px; letter-spacing: 0.08em; background: #0d131c;
	}
	.attributions .dim { color: #2a3947; }

	@media (max-width: 640px) {
		header { gap: 6px; padding: 6px 8px; }
		.brand-name em { display: none; }
		.venue-select { max-width: 150px; font-size: 10px; }
		.width-slider { width: 64px; }
		.weather-slot .ws-label { display: none; }
		.map-frame { margin: 8px 8px 6px; padding: 6px; border-radius: 13px; }
		:global(.map-canvas) { height: clamp(220px, 38vh, 420px); }
		.filter-chips { top: 10px; left: 46px; }
		.map-hint { font-size: 8px; bottom: 10px; }
		.feed-pane { padding: 8px; }
		.feed-slot { min-width: 100%; }
	}

	/* leaflet dark chrome */
	:global(.map-tiles) { filter: brightness(0.68) invert(1) contrast(2.7) hue-rotate(192deg) saturate(0.34) brightness(0.72); }
	:global(.leaflet-control-attribution) { background: rgba(10,10,10,0.8) !important; color: #667b90 !important; font-family: inherit; font-size: 9px; }
	:global(.leaflet-bar a) { background: #0d131c !important; color: #dbe4ee !important; border-color: #243447 !important; }
	:global(.leaflet-tooltip) { background: #111 !important; color: #FDB515 !important; border: 1px solid #243447 !important; font-family: inherit; font-size: 11px; }
</style>
